import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  X, Loader2, Download, Sparkles, RotateCcw, FileText, Brain, Target,
} from "lucide-react";

/**
 * AppealEditorModal
 * ------------------
 * Full-screen, dark-theme modal that lets the user preview & edit the
 * AI-drafted appeal letter for one Work Queue bucket BEFORE exporting it
 * as a PDF.
 *
 * Flow:
 *  1. Mount with a bucketId → GET /workqueue/{bucketId}/appeal-draft
 *  2. User edits subject / body / supporting docs / key arguments
 *  3. POST /workqueue/{bucketId}/appeal-pack/render → downloads PDF
 *  4. Click "Restore AI Draft" to reset to the original AI-generated text
 */
export default function AppealEditorModal({ bucketId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [bucket, setBucket] = useState(null);
  const [original, setOriginal] = useState(null);
  const [letter, setLetter] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/workqueue/${encodeURIComponent(bucketId)}/appeal-draft`);
        if (cancelled) return;
        setBucket(data.bucket);
        setLetter(data.letter);
        setOriginal(JSON.parse(JSON.stringify(data.letter)));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load appeal draft");
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [bucketId, onClose]);

  const restore = () => {
    if (!original) return;
    setLetter(JSON.parse(JSON.stringify(original)));
    toast.success("Restored AI draft");
  };

  const updateDoc = (i, val) => {
    setLetter((l) => ({
      ...l,
      supporting_documents: l.supporting_documents.map((d, idx) => (idx === i ? val : d)),
    }));
  };

  const removeDoc = (i) => {
    setLetter((l) => ({
      ...l,
      supporting_documents: l.supporting_documents.filter((_, idx) => idx !== i),
    }));
  };

  const addDoc = () => {
    setLetter((l) => ({ ...l, supporting_documents: [...(l.supporting_documents || []), ""] }));
  };

  const updateKa = (i, val) => {
    setLetter((l) => ({
      ...l,
      key_arguments: (l.key_arguments || []).map((d, idx) => (idx === i ? val : d)),
    }));
  };

  const removeKa = (i) => {
    setLetter((l) => ({
      ...l,
      key_arguments: (l.key_arguments || []).filter((_, idx) => idx !== i),
    }));
  };

  const addKa = () => {
    setLetter((l) => ({ ...l, key_arguments: [...(l.key_arguments || []), ""] }));
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const resp = await api.post(
        `/workqueue/${encodeURIComponent(bucketId)}/appeal-pack/render`,
        { letter, bucket_override: null },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = bucketId.replace(/[^a-zA-Z0-9_]/g, "_");
      a.download = `appeal_pack_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Appeal packet exported");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  // ----- styles (dark executive theme, inline so component is portable) -----
  const overlay = "fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-center";
  const shell = "w-full max-w-5xl my-6 mx-3 rounded-2xl overflow-hidden flex flex-col";
  const shellStyle = {
    background: "linear-gradient(160deg, #0B1524 0%, #122236 55%, #14283F 100%)",
    border: "1px solid rgba(94,234,212,0.15)",
    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9), 0 0 0 1px rgba(94,234,212,0.06)",
  };
  const labelCls = "text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-300 mb-1.5";
  const inputCls = "w-full bg-slate-900/60 border border-white/10 rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-400/50 focus:border-teal-400";
  const ScoreChip = ({ icon: Icon, label, value, color = "#5EEAD4" }) => (
    <div
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
      style={{ background: "rgba(20,184,166,0.10)", border: "1px solid rgba(20,184,166,0.30)" }}
    >
      <Icon className="h-4 w-4 flex-none" style={{ color }} />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-300">{label}</div>
        <div className="text-sm font-bold" style={{ color }}>{value}</div>
      </div>
    </div>
  );

  return (
    <div className={overlay} data-testid="appeal-editor-modal" role="dialog" aria-modal="true">
      <div className={shell} style={shellStyle}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
              <Sparkles className="h-3 w-3" />
              AI Appeal Draft · Editable
            </div>
            <h2 className="font-display text-xl font-semibold text-white mt-1 tracking-tight">
              {bucket ? `${bucket.payor} · ${bucket.category}` : "Loading…"}
            </h2>
            {bucket && (
              <div className="text-xs text-slate-300 mt-1">
                ${Number(bucket.dollars || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} · {bucket.volume} claims
                {bucket.top_carc && <> · CARC {bucket.top_carc}</>}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="appeal-editor-close"
            className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 text-slate-300">
              <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
              <div className="mt-3 text-sm">Loading AI draft…</div>
            </div>
          ) : letter ? (
            <>
              {/* AI scoring strip */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <ScoreChip icon={Brain} label="Recovery Confidence" value={`${letter.confidence_score ?? "—"}%`} />
                <ScoreChip icon={Target} label="Recovery Likelihood" value={`${letter.recovery_likelihood ?? "—"}%`} />
                <ScoreChip
                  icon={FileText}
                  label="Appeal Priority"
                  value={letter.appeal_priority || "—"}
                  color={letter.appeal_priority === "High" || letter.appeal_priority === "Critical" ? "#FDBA74" : "#5EEAD4"}
                />
              </div>

              {/* Subject */}
              <div>
                <div className={labelCls}>Subject Line</div>
                <input
                  type="text"
                  data-testid="appeal-subject-input"
                  className={inputCls}
                  value={letter.subject || ""}
                  onChange={(e) => setLetter({ ...letter, subject: e.target.value })}
                />
              </div>

              {/* Letter body */}
              <div>
                <div className={labelCls}>Appeal Letter — 5 Sections (edit freely)</div>
                <textarea
                  data-testid="appeal-body-textarea"
                  className={`${inputCls} min-h-[420px] font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap`}
                  value={letter.letter || ""}
                  onChange={(e) => setLetter({ ...letter, letter: e.target.value })}
                />
                <p className="text-[10px] text-slate-400 mt-1.5">
                  Tip: Section headings (DENIAL SUMMARY / CLINICAL OR OPERATIONAL JUSTIFICATION / SUPPORTING DOCUMENTATION INCLUDED / REQUESTED RESOLUTION / RE-ADJUDICATION REQUEST) render as bold blocks in the PDF.
                </p>
              </div>

              {/* Supporting Documents */}
              <div>
                <div className="flex items-center justify-between">
                  <div className={labelCls}>Supporting Documents Included</div>
                  <button
                    onClick={addDoc}
                    data-testid="appeal-add-doc"
                    className="text-[10px] uppercase tracking-wider font-semibold text-teal-300 hover:text-teal-200"
                  >+ Add row</button>
                </div>
                <div className="space-y-1.5">
                  {(letter.supporting_documents || []).map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className={inputCls}
                        value={d}
                        onChange={(e) => updateDoc(i, e.target.value)}
                        data-testid={`appeal-doc-input-${i}`}
                      />
                      <button
                        onClick={() => removeDoc(i)}
                        className="h-9 w-9 rounded-md text-slate-400 hover:text-red-300 hover:bg-white/5 flex-none"
                        aria-label="Remove"
                        title="Remove"
                      >
                        <X className="h-4 w-4 mx-auto" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Arguments */}
              <div>
                <div className="flex items-center justify-between">
                  <div className={labelCls}>Key Arguments — for peer-to-peer</div>
                  <button
                    onClick={addKa}
                    data-testid="appeal-add-ka"
                    className="text-[10px] uppercase tracking-wider font-semibold text-teal-300 hover:text-teal-200"
                  >+ Add row</button>
                </div>
                <div className="space-y-1.5">
                  {(letter.key_arguments || []).map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className={inputCls}
                        value={d}
                        onChange={(e) => updateKa(i, e.target.value)}
                        data-testid={`appeal-ka-input-${i}`}
                      />
                      <button
                        onClick={() => removeKa(i)}
                        className="h-9 w-9 rounded-md text-slate-400 hover:text-red-300 hover:bg-white/5 flex-none"
                        aria-label="Remove"
                      >
                        <X className="h-4 w-4 mx-auto" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-[11px] italic text-slate-400 pt-2 border-t border-white/5">
                {letter.disclaimer || "Draft prepared for provider review prior to submission."}
              </p>
            </>
          ) : null}
        </div>

        {/* Footer / actions */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10 bg-slate-900/40">
          <Button
            variant="ghost"
            onClick={restore}
            disabled={loading || exporting || !original}
            data-testid="appeal-editor-restore"
            className="text-slate-300 hover:text-white hover:bg-white/10"
          >
            <RotateCcw className="h-4 w-4 mr-1.5" /> Restore Original Draft
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="appeal-editor-cancel"
              className="border-white/20 text-slate-200 hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={exportPdf}
              disabled={loading || exporting}
              data-testid="appeal-editor-export"
              className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold uppercase tracking-wider rounded-md"
            >
              {exporting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Exporting…</>
                         : <><Download className="h-4 w-4 mr-1.5" /> Export PDF</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
